import { useState, useRef, useEffect } from 'react';
import { X, Trash2, Save, ArrowLeft } from '../../icons/wp-icons';
import { useIsMobile } from '../../hooks/useIsMobile';

const UNTERSCHRIFT_TYPEN = [
  'Auftragsbestätigung',
  'Abnahme',
  'Lieferung',
  'Kostenvoranschlag akzeptiert',
  'Sonstiges'
];

function SignaturePad({ onSave, onClose }) {
  const isMobile = useIsMobile();
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    typ: 'Auftragsbestätigung',
    signiertVon: '',
    notizen: ''
  });

  // Body Scroll Lock für Mobile
  useEffect(() => {
    if (isMobile) {
      // Scroll Position speichern
      const scrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      document.body.style.overflow = 'hidden';

      return () => {
        // Scroll Position wiederherstellen
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        document.body.style.overflow = '';
        window.scrollTo(0, scrollY);
      };
    }
  }, [isMobile]);

  // Canvas initialisieren
  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');

    // Canvas Größe setzen
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width;
    canvas.height = 256;

    // Zeichenstil setzen
    ctx.strokeStyle = '#000000';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    // Weißer Hintergrund
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  }, []);

  // Koordinaten aus Event holen (Mouse oder Touch)
  const getCoordinates = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();

    if (e.touches && e.touches.length > 0) {
      return {
        x: e.touches[0].clientX - rect.left,
        y: e.touches[0].clientY - rect.top
      };
    }

    return {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    };
  };

  // Zeichnen starten
  const startDrawing = (e) => {
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const { x, y } = getCoordinates(e);

    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
    setHasSignature(true);
  };

  // Zeichnen
  const draw = (e) => {
    if (!isDrawing) return;
    e.preventDefault();

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const { x, y } = getCoordinates(e);

    ctx.lineTo(x, y);
    ctx.stroke();
  };

  // Zeichnen beenden
  const stopDrawing = () => {
    setIsDrawing(false);
  };

  // Canvas löschen
  const clearCanvas = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');

    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    setHasSignature(false);
  };

  // Speichern
  const handleSave = async () => {
    if (!hasSignature) {
      alert('Bitte unterschreiben Sie zuerst.');
      return;
    }

    if (!formData.signiertVon.trim()) {
      alert('Bitte geben Sie einen Namen ein.');
      return;
    }

    setSaving(true);
    try {
      const canvas = canvasRef.current;
      const signatureData = canvas.toDataURL('image/png');

      await onSave({
        ...formData,
        signiertVon: formData.signiertVon.trim(),
        signatureData
      });
    } catch (error) {
      console.error('Fehler beim Speichern:', error);
      alert('Fehler beim Speichern: ' + (error.message || 'Unbekannter Fehler'));
    } finally {
      setSaving(false);
    }
  };

  // Mobile Fullscreen Version
  if (isMobile) {
    return (
      <div
        className="fixed inset-0 bg-white z-50 flex flex-col"
        style={{
          paddingTop: 'env(safe-area-inset-top, 0px)',
          paddingBottom: 'env(safe-area-inset-bottom, 0px)'
        }}
      >
        {/* Mobile Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
          <button
            onClick={onClose}
            className="p-2 -ml-2 hover:bg-gray-100 rounded-lg transition"
          >
            <ArrowLeft className="w-6 h-6" />
          </button>
          <h2 className="text-lg font-semibold text-gray-900">Unterschrift</h2>
          <button
            onClick={clearCanvas}
            className="p-2 -mr-2 text-red-600 hover:bg-red-50 rounded-lg transition"
          >
            <Trash2 className="w-5 h-5" />
          </button>
        </div>

        {/* Mobile Content */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* Kompakte Form-Felder */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Typ</label>
              <select
                value={formData.typ}
                onChange={(e) => setFormData({ ...formData, typ: e.target.value })}
                className="w-full px-3 py-3 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-primary focus:border-transparent"
              >
                {UNTERSCHRIFT_TYPEN.map((typ) => (
                  <option key={typ} value={typ}>{typ}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Name *</label>
              <input
                type="text"
                value={formData.signiertVon}
                onChange={(e) => setFormData({ ...formData, signiertVon: e.target.value })}
                placeholder="Name eingeben"
                className="w-full px-3 py-3 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>
          </div>

          {/* Großer Canvas für Touch */}
          <div ref={containerRef} className="flex-1">
            <div className="border-2 border-gray-300 rounded-xl overflow-hidden bg-white">
              <canvas
                ref={canvasRef}
                className="w-full cursor-crosshair touch-none"
                style={{ height: '200px' }}
                onMouseDown={startDrawing}
                onMouseMove={draw}
                onMouseUp={stopDrawing}
                onMouseLeave={stopDrawing}
                onTouchStart={startDrawing}
                onTouchMove={draw}
                onTouchEnd={stopDrawing}
              />
            </div>
            <p className="text-xs text-gray-500 mt-2 text-center">
              Mit dem Finger unterschreiben
            </p>
          </div>

          {/* Optionale Notizen */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Notizen (optional)</label>
            <textarea
              value={formData.notizen}
              onChange={(e) => setFormData({ ...formData, notizen: e.target.value })}
              placeholder="Zusätzliche Anmerkungen..."
              rows={2}
              className="w-full px-3 py-3 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
            />
          </div>
        </div>

        {/* Mobile Footer - mehr Abstand für iPhone Home-Leiste */}
        <div
          className="px-4 pt-4 border-t border-gray-200 bg-gray-50"
          style={{ paddingBottom: 'calc(80px + env(safe-area-inset-bottom, 34px))' }}
        >
          <button
            onClick={handleSave}
            disabled={saving || !hasSignature || !formData.signiertVon.trim()}
            className="w-full flex items-center justify-center gap-2 px-6 py-4 bg-primary text-white rounded-xl font-medium disabled:opacity-50 disabled:cursor-not-allowed active:bg-primary-dark"
          >
            {saving ? (
              <>
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Speichern...
              </>
            ) : (
              <>
                <Save className="w-5 h-5" />
                Unterschrift speichern
              </>
            )}
          </button>
        </div>
      </div>
    );
  }

  // Desktop Version
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-bold text-gray-900">Unterschrift hinzufügen</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Typ-Auswahl */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Unterschriften-Typ *
            </label>
            <select
              value={formData.typ}
              onChange={(e) => setFormData({ ...formData, typ: e.target.value })}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent"
            >
              {UNTERSCHRIFT_TYPEN.map((typ) => (
                <option key={typ} value={typ}>
                  {typ}
                </option>
              ))}
            </select>
          </div>

          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Name des Unterzeichners *
            </label>
            <input
              type="text"
              value={formData.signiertVon}
              onChange={(e) => setFormData({ ...formData, signiertVon: e.target.value })}
              placeholder="z.B. Max Mustermann"
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent"
            />
          </div>

          {/* Signatur-Canvas */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-700">
                Unterschrift *
              </label>
              <button
                onClick={clearCanvas}
                className="flex items-center gap-1 text-sm text-gray-500 hover:text-red-600 transition"
              >
                <Trash2 className="w-4 h-4" />
                Zurücksetzen
              </button>
            </div>
            <div className="border-2 border-gray-300 rounded-xl overflow-hidden bg-white">
              <canvas
                ref={canvasRef}
                className="w-full cursor-crosshair touch-none"
                style={{ height: '256px' }}
                onMouseDown={startDrawing}
                onMouseMove={draw}
                onMouseUp={stopDrawing}
                onMouseLeave={stopDrawing}
                onTouchStart={startDrawing}
                onTouchMove={draw}
                onTouchEnd={stopDrawing}
              />
            </div>
            <p className="text-xs text-gray-500 mt-2">
              Unterschreiben Sie mit der Maus oder dem Finger auf dem Touchscreen.
            </p>
          </div>

          {/* Notizen */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Notizen (optional)
            </label>
            <textarea
              value={formData.notizen}
              onChange={(e) => setFormData({ ...formData, notizen: e.target.value })}
              placeholder="Zusätzliche Anmerkungen..."
              rows={3}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 bg-gray-50 rounded-b-2xl">
          <button
            onClick={onClose}
            className="px-6 py-3 text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 transition font-medium"
          >
            Abbrechen
          </button>
          <button
            onClick={handleSave}
            disabled={saving || !hasSignature || !formData.signiertVon.trim()}
            className="flex items-center gap-2 px-6 py-3 bg-primary text-white rounded-xl hover:bg-primary-dark transition font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? (
              <>
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Speichern...
              </>
            ) : (
              <>
                <Save className="w-5 h-5" />
                Unterschrift speichern
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

export default SignaturePad;
